import React from 'react';
import { FileText, Lock, X, Plus } from 'lucide-react';
import { sanitizeErpText } from '../../utils/formatting';

// Print destinations mirror the A+ Comments screen (Print Code: Invoice / Pick List / Both / Internal).
// Values are semantic; the backend maps them to the single-char A+ codes when building cXML.
// NOTE: exact cXML representation (multi-line + print code) is pending confirmation from Trey.
export const PRINT_CODES = [
  { value: 'both', label: 'Both' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'picklist', label: 'Pick List' },
  { value: 'internal', label: 'Internal' },
];

const MAX_LINES = 4;      // A+ offers 4 order-comment lines; beyond that CSRs use A+ directly
const MAX_LEN = 62;       // A+ comment text field width (INCMTX char(62))

// Order-level comments — written into the body of the order as A+ comment lines.
// Each line carries its own print code; "Internal" keeps it AP-only.
export default function OrderComments({ orderComments, onOrderCommentsChange }) {
  const lines = orderComments || [];

  const updateLine = (i, patch) =>
    onOrderCommentsChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const addLine = () => {
    if (lines.length >= MAX_LINES) return;
    onOrderCommentsChange([...lines, { text: '', printCode: 'both' }]);
  };

  const removeLine = (i) => onOrderCommentsChange(lines.filter((_, idx) => idx !== i));

  return (
    <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1">
          <FileText className="w-3 h-3" /> Order Comments
        </h3>
        <span className="text-xs text-slate-500">up to {MAX_LINES} lines · printed where you choose</span>
      </div>

      <div className="space-y-2">
        {lines.map((line, i) => {
          const isInternal = line.printCode === 'internal';
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="w-4 text-right text-xs text-slate-500">{i + 1}</span>
              <div className="relative flex-1">
                <input
                  type="text"
                  maxLength={MAX_LEN}
                  value={line.text}
                  placeholder="Add a comment…"
                  onChange={(e) => updateLine(i, { text: sanitizeErpText(e.target.value, MAX_LEN) })}
                  className={`w-full bg-slate-700 text-white text-sm rounded pl-2 pr-12 py-1 border focus:border-amber-500 focus:outline-none ${
                    isInternal ? 'border-amber-500/50' : 'border-slate-600'
                  }`}
                />
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                  {line.text.length}/{MAX_LEN}
                </span>
              </div>
              <select
                value={line.printCode}
                onChange={(e) => updateLine(i, { printCode: e.target.value })}
                title="Where this comment prints"
                className={`text-xs rounded px-1.5 py-1 border focus:border-amber-500 focus:outline-none ${
                  isInternal
                    ? 'bg-amber-500/10 border-amber-500/50 text-amber-400'
                    : 'bg-slate-700 border-slate-600 text-slate-300'
                }`}
              >
                {PRINT_CODES.map((pc) => (
                  <option key={pc.value} value={pc.value}>{pc.label}</option>
                ))}
              </select>
              <button
                onClick={() => removeLine(i)}
                className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0"
                title="Remove comment line"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {lines.length < MAX_LINES && (
        <button
          onClick={addLine}
          className="mt-2 flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add comment line
        </button>
      )}

      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
        <Lock className="w-3 h-3 text-amber-400 flex-shrink-0" />
        “Internal” lines are AP-only — they won’t print for the customer.
      </div>
    </div>
  );
}
